import React, { useCallback, useEffect, useRef, useState } from 'react';

import { DataState, Skeleton } from '../components/loading';
import useDocumentTitle from '../hooks/useDocumentTitle';
import { getStatus } from '../services/status';
import { getReleaseFeed } from '../services/releaseFeed';
import { BANNER_TEXT, HEALTH, normalizeStatus } from '../utils/status';
import { componentLabel, formatReleaseDate, normalizeReleaseFeed } from '../utils/releaseFeed';
import { webVersion } from '../config/buildInfo';
import StatusUptime from '../components/public/StatusUptime';

/** How often the page asks again while it is open. */
export const REFRESH_MS = 60 * 1000;

/**
 * /status — keepr's public status page (KPR-320): is keepr up, and which
 * version of each part is running. Public and signed out, in the public shell.
 *
 * Everything comes from GET /api/status except the web row: the endpoint
 * reports the platform's server-side parts, and this page is the one place
 * that knows which build of the web client is on screen (config/buildInfo.js).
 *
 * It says only what the endpoint says. A failed request, or a body that is not
 * the contract's shape, reads "keepr's API is unreachable" and nothing more:
 * no error text, address or status code reaches a public page.
 *
 * The first wait is drawn with DataState (platform-standards §14). A failed ask
 * is not that component's error state: it is this page's answer, the banner.
 *
 * It asks again every minute while open (the endpoint is cached for up to one),
 * keeping the last good answer on screen between asks, and does not ask while
 * the tab is hidden.
 *
 * A row the endpoint has an `uptime` entry for (KPR-758), the web row
 * included, also shows its 30- and 90-day uptime, a 90-day bar and its latest
 * incidents (components/public/StatusUptime.js). Measured by Route 53 from
 * outside keepr; without an entry the row is as it was.
 *
 * Recent changes, below the components (KPR-791), comes from the public
 * release feed: /release-feed.json, a static file the web build writes from
 * the deploy ledgers' public lines. It is read once, with no credentials, so
 * every visitor, signed in or out, sees the same entries. Its failure is its
 * own: the rest of the page renders whatever the feed does. Nothing on this
 * page links anywhere, least of all to the admin-only change log.
 */
export default function Status() {
	useDocumentTitle('Status');
	const [state, setState] = useState({ phase: 'loading', data: null });
	const alive = useRef(true);

	const load = useCallback(async () => {
		let data = null;
		try {
			data = normalizeStatus(await getStatus());
		} catch {
			data = null;
		}
		if (!alive.current) return;
		setState(data ? { phase: 'ready', data } : { phase: 'unreachable', data: null });
	}, []);

	useEffect(() => {
		alive.current = true;
		load();
		const timer = setInterval(() => {
			if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
			load();
		}, REFRESH_MS);
		return () => {
			alive.current = false;
			clearInterval(timer);
		};
	}, [load]);

	const [changes, setChanges] = useState({ phase: 'loading', releases: [] });
	useEffect(() => {
		let live = true;
		(async () => {
			let releases = null;
			try {
				releases = normalizeReleaseFeed(await getReleaseFeed());
			} catch {
				releases = null;
			}
			if (live) setChanges(releases ? { phase: releases.length ? 'ready' : 'empty', releases } : { phase: 'unreadable', releases: [] });
		})();
		return () => {
			live = false;
		};
	}, []);

	const { phase, data } = state;
	const level = phase === 'ready' ? data.status : 'unreachable';

	const rows = [{ id: 'web', name: 'keepr web app', version: webVersion, status: 'ok' }];
	if (phase === 'ready') rows.push(...data.components);
	// Uptime measured from outside keepr (KPR-758), by component id; the web row's too.
	const uptime = phase === 'ready' && data.uptime ? data.uptime : {};

	return (
		<div className="status-page">
			<h1>keepr status</h1>
			<DataState
				state={phase === 'loading' ? 'loading-initial' : 'ready'}
				skeleton={<Skeleton variant="block" />}
				onRetry={load}
				label="keepr status"
				className="data-state--public"
			>
				<div className={`status-page__banner status-page__banner--${level}`} role="status" aria-live="polite">
					{BANNER_TEXT[level]}
				</div>

				{phase === 'unreachable' ? (
					<p className="status-page__note">
						This page could not get an answer from keepr&apos;s API, so it cannot say which parts are running.
						It tries again every minute.
					</p>
				) : null}

				{phase !== 'loading' ? (
					<section aria-labelledby="status-components">
						<h2 id="status-components">Components</h2>
						<ul className="status-page__list">
							{rows.map(row => (
								<li key={row.id} className="status-page__row">
									<span className="status-page__name">{row.name}</span>
									<span className="status-page__version">{row.version || '—'}</span>
									<span className={`status-page__health status-page__health--${row.status}`}>{HEALTH[row.status]}</span>
									{uptime[row.id] ? <StatusUptime uptime={uptime[row.id]} /> : null}
								</li>
							))}
						</ul>
						{phase === 'ready' && data.checkedAt ? (
							<p className="status-page__note">
								Checked <time dateTime={data.checkedAt}>{new Date(data.checkedAt).toLocaleString()}</time>.
							</p>
						) : null}
					</section>
				) : null}
			</DataState>

			<section aria-labelledby="status-changes" className="status-page__changes">
				<h2 id="status-changes">Recent changes</h2>
				<DataState
					state={changes.phase === 'loading' ? 'loading-initial' : changes.phase === 'empty' ? 'empty' : 'ready'}
					skeleton={<Skeleton variant="block" />}
					empty={<p className="status-page__note">No changes have been published yet.</p>}
					label="Recent changes"
					className="data-state--public"
				>
					{changes.phase === 'unreadable' ? (
						<p className="status-page__note">Recent changes could not be read just now. The rest of this page is not affected.</p>
					) : (
						<ol className="status-page__releases">
							{changes.releases.map(release => (
								<li key={`${release.date}-${release.version}`} className="status-page__release">
									<div className="status-page__release-head">
										<time dateTime={release.date}>{formatReleaseDate(release.date)}</time>
										<span className="status-page__version">{componentLabel(release.components)} {release.version}</span>
									</div>
									<ul className="status-page__release-lines">
										{release.lines.map(line => <li key={line}>{line}</li>)}
									</ul>
								</li>
							))}
						</ol>
					)}
				</DataState>
			</section>
		</div>
	);
}
